import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Menu,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  OutlinedInput,
  Switch,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CloseIcon from "@mui/icons-material/Close";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import {
  listSubjects,
  listCourses,
  createSubject,
  updateSubject,
  previewDelete,
  confirmDelete,
  apiErrorMessage,
} from "../../api/assessmentHierarchy";
import useDebouncedValue from "./useDebouncedValue";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
import BulkImportButton from "./BulkImportButton";
const ACCENT = "#17479e";

const allColumns = [
  { key: "id", label: "ID", cellCss: { minWidth: "60px", width: "60px" }, getValue: (r) => r.id },
  { key: "name", label: "Name", cellCss: { align: "left", textAlign: "left", minWidth: "180px" }, getValue: (r) => r.name },
  { key: "description", label: "Description", cellCss: { minWidth: "240px" }, getValue: (r) => r.description },
  { key: "linkedCourses", label: "Exam", cellCss: { minWidth: "200px" } },
  { key: "chapters", label: "Chapters", cellCss: { align: "center", minWidth: "100px" }, getValue: (r) => r.chapterCount },
  { key: "status", label: "Status", cellCss: { align: "center", minWidth: "120px" }, getValue: (r) => r.status },
];

const defaultVisibleKeys = new Set(allColumns.map((c) => c.key));
const STORAGE_KEY = "assessmentSubjectsHiddenColumns";

const statusCfg = {
  ACTIVE: { label: "Active", color: "#2e7d32", bg: "#e8f5e9" },
  INACTIVE: { label: "Inactive", color: "#555", bg: "#f0f0f0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

function SubjectFormDialog({ open, subject, courses, onClose, onSave }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");
  const [courseIds, setCourseIds] = useState([]);
  const [status, setStatus] = useState("ACTIVE");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setName(subject?.name || "");
    setDescription(subject?.description || "");
    setDisplayOrder("");
    setCourseIds(subject?.courseIds || []);
    setStatus(subject?.status || "ACTIVE");
  }, [open, subject]);

  const handleSubmit = async () => {
    if (!name.trim() || courseIds.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        id: subject?.id,
        name: name.trim(),
        description: description.trim(),
        courseIds,
        displayOrder: displayOrder === "" ? undefined : Number(displayOrder),
        active: status === "ACTIVE",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 2.5,
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          >
            <MenuBookOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>
              {subject ? "Edit Subject" : "Add Subject"}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
              {subject ? "Update name, exams and status" : "Name it, link exams and set status"}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <DialogContent sx={{ pt: 3, backgroundColor: "#f7f8fb" }}>
        <Card sx={{ boxShadow: 1, borderRadius: 3, overflow: "hidden" }}>
          <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
            <TextField
              label="Subject Name"
              fullWidth
              size="small"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <TextField
              label="Display Order (optional)"
              type="number"
              fullWidth
              size="small"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <FormControl fullWidth size="small" required>
              <InputLabel>Linked Exams</InputLabel>
              <Select
                multiple
                label="Linked Exams"
                value={courseIds}
                onChange={(e) => setCourseIds(e.target.value)}
                input={<OutlinedInput label="Linked Exams" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((cid) => (
                      <Chip key={cid} label={courses.find((c) => c.id === cid)?.name || cid} size="small" />
                    ))}
                  </Box>
                )}
              >
                {courses.map((course) => (
                  <MenuItem key={course.id} value={course.id}>
                    <Checkbox size="small" checked={courseIds.includes(course.id)} sx={{ mr: 1, p: 0.5 }} />
                    {course.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: status === "ACTIVE" ? "#2e7d32" : "divider",
                backgroundColor: status === "ACTIVE" ? "#eaf7ec" : "#f5f7fb",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: status === "ACTIVE" ? "#2e7d32" : "#555" }}>
                  {status === "ACTIVE" ? "Active" : "Inactive"}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                  {status === "ACTIVE" ? "Visible and usable in assessments" : "Hidden from assessments"}
                </Typography>
              </Box>
              <Switch
                checked={status === "ACTIVE"}
                onChange={(e) => setStatus(e.target.checked ? "ACTIVE" : "INACTIVE")}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2e7d32" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2e7d32" },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              flex: "0 0 auto",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: "#555",
              backgroundColor: "#eceff1",
              "&:hover": { backgroundColor: "#e0e3e6" },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submitting}
            onClick={handleSubmit}
            disabled={!name.trim() || courseIds.length === 0}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
              "&:hover": { background: `linear-gradient(135deg, #123a80 0%, #0d47a1 100%)` },
              "&.Mui-disabled": { background: "#b0bec5" },
            }}
          >
            {subject ? "Save Changes" : "Create Subject"}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function AssessmentSubjects() {
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [courseFilter, setCourseFilter] = useState("");
  const [publishFilter, setPublishFilter] = useState("");
  const [orderBy, setOrderBy] = useState("id");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState("");

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const hidden = JSON.parse(raw);
        if (Array.isArray(hidden)) {
          const visible = allColumns.map((c) => c.key).filter((k) => !hidden.includes(k));
          if (visible.length > 0) return new Set(visible);
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  const loadCourses = () => {
    listCourses({ includeInactive: true })
      .then(setCourses)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load exams")));
  };

  const loadSubjects = () => {
    setLoading(true);
    listSubjects({
      page,
      size: rowsPerPage,
      courseId: courseFilter,
      isActive: publishFilter ? publishFilter === "ACTIVE" : undefined,
      search: debouncedSearch,
    })
      .then((data) => {
        setSubjects(data.content);
        setTotalElements(data.totalElements ?? data.content.length);
      })
      .catch((err) => {
        setSubjects([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load subjects"));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCourses();
  }, []);

  useEffect(() => {
    loadSubjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, courseFilter, publishFilter, debouncedSearch]);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(allColumns.map((c) => c.key).filter((k) => !visibleColumns.has(k))),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  const sortedData = useMemo(() => {
    return [...subjects].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [subjects, orderBy, order]);

  const handleSort = (columnKey) => {
    if (columnKey === "linkedCourses") return;
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const paginatedData = sortedData;

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (subject) => {
    setEditing(subject);
    setFormOpen(true);
  };

  const handleSave = async (values) => {
    const { id, ...payload } = values;
    try {
      if (id) await updateSubject(id, payload);
      else await createSubject(payload);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to save subject"));
      throw err;
    }
    toast.success(id ? "Subject updated successfully!" : "Subject created successfully!");
    setFormOpen(false);
    loadSubjects();
  };

  const handleDeleteClick = async (row) => {
    try {
      const preview = await previewDelete("subject", row.id);
      setDeleteMessage(preview?.message || "");
      setDeleteId(row.id);
    } catch (err) {
      toast.error(apiErrorMessage(err, "This subject cannot be deleted"));
    }
  };

  const handleDelete = () => {
    confirmDelete("subject", deleteId)
      .then((res) => {
        toast.success(res?.message || "Subject deleted!");
        setDeleteId(null);
        if (subjects.length === 1 && page > 0) setPage(page - 1);
        else loadSubjects();
      })
      .catch((err) => {
        setDeleteId(null);
        toast.error(apiErrorMessage(err, "Failed to delete subject"));
      });
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                flex: "1 1 560px",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                placeholder="Search names"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Exam</InputLabel>
                <Select
                  label="Exam"
                  value={courseFilter}
                  onChange={(e) => {
                    setCourseFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All Exams</MenuItem>
                  {courses.map((course) => (
                    <MenuItem key={course.id} value={course.id}>
                      {course.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={publishFilter}
                  onChange={(e) => {
                    setPublishFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{
                  minWidth: { xs: "120px", md: "auto" },
                  fontSize: { xs: "10px", md: "12px" },
                }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{
                  sx: {
                    width: 200,
                    maxHeight: 500,
                    overflowY: "auto",
                    borderRadius: 2,
                  },
                }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={
                      <Checkbox
                        checked={visibleColumns.has(col.key)}
                        onChange={() => toggleColumn(col.key)}
                      />
                    }
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <BulkImportButton onImported={loadSubjects} />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
                sx={{
                  minWidth: { xs: "120px", md: "auto" },
                  fontSize: { xs: "10px", md: "12px" },
                }}
              >
                Add Subject
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: 2,
          borderRadius: 3,
        }}
      >
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)", minHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    sortDirection={orderBy === c.key ? order : false}
                  >
                    {c.key === "linkedCourses" ? (
                      c.label
                    ) : (
                      <TableSortLabel
                        active={orderBy === c.key}
                        direction={orderBy === c.key ? order : "asc"}
                        onClick={() => handleSort(c.key)}
                        sx={{
                          color: "#ffffff !important",
                          "&.Mui-active": { color: "#ffffff !important" },
                          "& .MuiTableSortLabel-icon": {
                            color: "#ffffff !important",
                          },
                        }}
                      >
                        {c.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: "110px",
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumnDefs.length + 1}
                    sx={{ textAlign: "center", py: 4 }}
                  >
                    <AssessmentEmptyState message="No subjects found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "rgba(0,0,0,0.02)",
                      },
                    }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.cellCss?.align}
                        sx={{ padding: "10px 12px", ...c.cellCss }}
                      >
                        {(() => {
                          if (c.key === "name") {
                            return (
                              <Typography
                                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                              >
                                {row.name}
                              </Typography>
                            );
                          }
                          if (c.key === "id") {
                            return (
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  color: "text.secondary",
                                }}
                              >
                                {row.id}
                              </Typography>
                            );
                          }
                          if (c.key === "description") {
                            const value = c.getValue(row);
                            return (
                              <Tooltip title={value || ""} arrow disableHoverListener={!value}>
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    color: "text.secondary",
                                    maxWidth: 240,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {value || "—"}
                                </Typography>
                              </Tooltip>
                            );
                          }
                          if (c.key === "linkedCourses") return row.courseNames.join(", ") || "—";
                          if (c.key === "status")
                            return <StatusChip value={row.status} />;
                          return c.getValue(row);
                        })()}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{
                        padding: "10px 12px",
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#fff",
                        zIndex: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <Tooltip title="Edit Subject" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEdit(row)}
                            sx={{
                              "&:hover": {
                                backgroundColor: "primary.light",
                                color: "white",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Subject" arrow placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(row)}
                            sx={{
                              "&:hover": {
                                backgroundColor: "error.light",
                                color: "white",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": {
              paddingLeft: 2,
              paddingRight: 2,
            },
          }}
        />
      </Paper>

      <SubjectFormDialog
        open={formOpen}
        subject={editing}
        courses={courses}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={deleteMessage || "This action cannot be undone."}
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default AssessmentSubjects;
